import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore,
  Save,
  Refresh,
  Storage,
  Cloud,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../contexts/ApiContext';

interface FieldMetadata {
  type: 'string' | 'number' | 'boolean' | 'datetime' | 'select';
  label: string;
  editable: boolean;
  required?: boolean;
  options?: string[];
  source: 'cognito' | 'dynamo';
}

interface FieldMetadataMap {
  [key: string]: FieldMetadata;
}

interface RawEntryData {
  entityType: string;
  entityId: string;
  cognito: any;
  dynamo: any;
  fieldMetadata: {
    cognito?: FieldMetadataMap;
    dynamo?: FieldMetadataMap;
  };
  timestamp: string;
}

interface AdvancedDBEditorProps {
  entityType: string;
  entityId: string;
  onSaveSuccess?: () => void;
}

const AdvancedDBEditor: React.FC<AdvancedDBEditorProps> = ({
  entityType,
  entityId,
  onSaveSuccess,
}) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const [editedCognitoData, setEditedCognitoData] = useState<any>({});
  const [editedDynamoData, setEditedDynamoData] = useState<any>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch raw database entry
  const { data, isLoading, error, refetch } = useQuery<RawEntryData>({
    queryKey: ['rawEntry', entityType, entityId],
    queryFn: () => api.get(`/admin/database/${entityType}/${entityId}/raw`),
  });

  // Initialize edited data when data loads
  useEffect(() => {
    if (data) {
      setEditedCognitoData(data.cognito?.attributes || {});
      setEditedDynamoData(data.dynamo || {});
      setHasChanges(false);
    }
  }, [data]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (updates: { cognitoUpdates: any; dynamoUpdates: any }) =>
      api.put(`/admin/database/${entityType}/${entityId}/raw`, updates),
    onSuccess: () => {
      queryClient.invalidateQueries(['rawEntry', entityType, entityId]);
      queryClient.invalidateQueries([entityType, entityId]);
      setHasChanges(false);
      if (onSaveSuccess) {
        onSaveSuccess();
      }
    },
  });

  const handleCognitoFieldChange = (fieldName: string, value: any) => {
    setEditedCognitoData((prev: any) => ({
      ...prev,
      [fieldName]: value,
    }));
    setHasChanges(true);
  };

  const handleDynamoFieldChange = (fieldName: string, value: any) => {
    setEditedDynamoData((prev: any) => ({
      ...prev,
      [fieldName]: value,
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    // Only send changed fields
    const cognitoUpdates: any = {};
    const dynamoUpdates: any = {};

    // Compare Cognito fields
    if (data?.cognito?.attributes) {
      Object.keys(editedCognitoData).forEach((key) => {
        if (editedCognitoData[key] !== data.cognito.attributes[key]) {
          cognitoUpdates[key] = editedCognitoData[key];
        }
      });
    }

    // Compare DynamoDB fields
    if (data?.dynamo) {
      Object.keys(editedDynamoData).forEach((key) => {
        if (editedDynamoData[key] !== data.dynamo[key]) {
          dynamoUpdates[key] = editedDynamoData[key];
        }
      });
    }

    updateMutation.mutate({ cognitoUpdates, dynamoUpdates });
  };

  const handleReset = () => {
    if (data) {
      setEditedCognitoData(data.cognito?.attributes || {});
      setEditedDynamoData(data.dynamo || {});
      setHasChanges(false);
    }
  };

  const renderField = (
    fieldName: string,
    metadata: FieldMetadata,
    value: any,
    onChange: (value: any) => void
  ) => {
    const { type, label, editable, options } = metadata;

    if (!editable) {
      return (
        <TextField
          fullWidth
          label={label}
          value={value || ''}
          disabled
          size="small"
          InputProps={{
            readOnly: true,
          }}
        />
      );
    }

    switch (type) {
      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={value === 'true' || value === true}
                onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
              />
            }
            label={label}
          />
        );

      case 'select':
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{label}</InputLabel>
            <Select
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              label={label}
            >
              {options?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'number':
        return (
          <TextField
            fullWidth
            label={label}
            type="number"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            size="small"
          />
        );

      case 'datetime':
        return (
          <TextField
            fullWidth
            label={label}
            value={value || ''}
            disabled
            size="small"
            InputProps={{
              readOnly: true,
            }}
          />
        );

      case 'string':
      default:
        return (
          <TextField
            fullWidth
            label={label}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            size="small"
          />
        );
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load database entry. Please try again.
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  const { fieldMetadata } = data;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Advanced Database Editor
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => refetch()}
            disabled={updateMutation.isPending}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={!hasChanges || updateMutation.isPending}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {/* Status Messages */}
      {updateMutation.isSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Database entry updated successfully!
        </Alert>
      )}
      {updateMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to update database entry. Please try again.
        </Alert>
      )}
      {hasChanges && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved changes
        </Alert>
      )}

      {/* Cognito Fields */}
      {fieldMetadata.cognito && Object.keys(fieldMetadata.cognito).length > 0 && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Cloud color="primary" />
              <Typography variant="h6">Cognito Attributes</Typography>
              <Chip label={`${Object.keys(fieldMetadata.cognito).length} fields`} size="small" />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {Object.entries(fieldMetadata.cognito).map(([fieldName, metadata]) => (
                <Grid item xs={12} sm={6} md={4} key={fieldName}>
                  {renderField(
                    fieldName,
                    metadata,
                    editedCognitoData[fieldName],
                    (value) => handleCognitoFieldChange(fieldName, value)
                  )}
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* DynamoDB Fields */}
      {fieldMetadata.dynamo && Object.keys(fieldMetadata.dynamo).length > 0 && (
        <Accordion defaultExpanded sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Storage color="secondary" />
              <Typography variant="h6">DynamoDB Fields</Typography>
              <Chip label={`${Object.keys(fieldMetadata.dynamo).length} fields`} size="small" />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {Object.entries(fieldMetadata.dynamo).map(([fieldName, metadata]) => (
                <Grid item xs={12} sm={6} md={4} key={fieldName}>
                  {renderField(
                    fieldName,
                    metadata,
                    editedDynamoData[fieldName],
                    (value) => handleDynamoFieldChange(fieldName, value)
                  )}
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default AdvancedDBEditor;

